import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import useSettings from '../utils/useSettings';
import { trackLandingCta, trackStoryOfDayReadClick, trackStoryOfDayListen } from '../utils/analytics';

// Landing-page "Story of the Day" — one community story featured daily, full
// text + illustration + browser read-aloud, no signup needed. The full text
// stays in the DOM (collapsed via max-height) so search engines index fresh
// content every day. Renders nothing when no community story is available
// or the community feature flag is off.
const StoryOfTheDay = () => {
  const { settings } = useSettings();
  const [story, setStory] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const communityEnabled = settings.featureFlags?.community !== false;

  useEffect(() => {
    let mounted = true;
    API.get('/stories/story-of-the-day')
      .then(res => { if (mounted && res.data.story) setStory(res.data.story); })
      .catch(() => {}); // silent — section simply doesn't render
    return () => { mounted = false; };
  }, []);

  // Stop speech when leaving the page
  const playingRef = useRef(false);
  playingRef.current = isPlaying;
  useEffect(() => () => { if (playingRef.current) window.speechSynthesis?.cancel(); }, []);

  if (!story || !communityEnabled) return null;

  const handleListen = () => {
    if (!window.speechSynthesis) return;
    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(`${story.title}. ${story.content}`);
    utt.rate = 0.88;
    utt.pitch = 1.05;
    utt.lang = story.language || 'en';
    if ((story.language || 'en') === 'en') {
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find(v =>
        v.lang.startsWith('en') && /samantha|karen|victoria|moira|fiona|daniel|google uk|google us/i.test(v.name)
      ) || voices.find(v => v.lang.startsWith('en'));
      if (preferred) utt.voice = preferred;
    }
    utt.onend = () => setIsPlaying(false);
    utt.onerror = () => setIsPlaying(false);
    window.speechSynthesis.speak(utt);
    setIsPlaying(true);
    trackStoryOfDayListen(story.slug);
  };

  const paragraphs = story.content.split(/\n\n+/).filter(p => p.trim());
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const metaParts = [
    story.child?.name && `A story for ${story.child?.name}`,
    story.genre,
    story.readingTime && `${story.readingTime} min read`,
    story.likes > 0 && `❤️ ${story.likes}`,
  ].filter(Boolean);

  return (
    <section className="py-5" style={{background:'var(--purple-section)'}}>
      <div className="container py-3">
        <div className="text-center mb-4">
          <span className="section-badge">🌟 Story of the Day</span>
          <h2 className="fw-bold mb-1" style={{fontFamily:"'Fredoka One',cursive", color:'var(--text-main)', fontSize:'clamp(28px,4vw,40px)'}}>
            Tonight's free story
          </h2>
          <p style={{color:'var(--text-muted2)', fontSize:'15px', margin:0}}>
            {today} — a new community story every day. No signup needed.
          </p>
        </div>

        <div className="mx-auto" style={{maxWidth:'860px'}}>
          <div className="card border-0 shadow-lg overflow-hidden" style={{borderRadius:'24px', background:'var(--card-bg)'}}>
            <div className="row g-0">

              {/* Illustration */}
              <div className="col-lg-5">
                {story.illustrationUrl ? (
                  <img
                    src={story.illustrationUrl}
                    alt={`Illustration for ${story.title}`}
                    style={{width:'100%', height:'100%', minHeight:'260px', objectFit:'cover'}}
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100"
                    style={{background:'linear-gradient(135deg,#667eea,#764ba2)', minHeight:'260px'}}>
                    <div className="text-center text-white p-4">
                      <div style={{fontSize:'64px', marginBottom:'8px'}}>🌙</div>
                      <div style={{fontFamily:"'Fredoka One',cursive", fontSize:'16px', opacity:0.9}}>{story.theme || 'A bedtime story'}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Story */}
              <div className="col-lg-7">
                <div className="p-4 p-md-5">
                  <h3 className="fw-bold mb-2" style={{fontFamily:"'Fredoka One',cursive", color:'var(--text-main)', fontSize:'clamp(20px,2.5vw,26px)'}}>
                    {story.title}
                  </h3>
                  {metaParts.length > 0 && (
                    <div className="mb-3" style={{color:'var(--text-soft)', fontSize:'13px'}}>
                      {metaParts.join(' · ')}
                    </div>
                  )}

                  {/* Full text in the DOM (SEO); visually collapsed — the
                      read button opens the story's own community page */}
                  <div style={{position:'relative'}}>
                    <div style={{maxHeight:'240px', overflow:'hidden'}}>
                      {paragraphs.map((p, i) => (
                        <p key={i} style={{color:'var(--text-body)', fontSize:'15px', lineHeight:1.9, marginBottom:'16px'}}>
                          {p}
                        </p>
                      ))}
                      {story.moralLesson && (
                        <p className="fst-italic" style={{color:'var(--text-muted2)', fontSize:'14px', borderLeft:'3px solid var(--border-purple2)', paddingLeft:'12px'}}>
                          ✨ {story.moralLesson}
                        </p>
                      )}
                    </div>
                    <div style={{position:'absolute', bottom:0, left:0, right:0, height:'110px', background:'linear-gradient(to bottom, transparent, var(--card-bg))', pointerEvents:'none'}}></div>
                  </div>

                  <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                    {story.slug && (
                      <Link to={`/community/${story.slug}`} className="btn btn-dream rounded-pill px-4 fw-semibold"
                        onClick={() => trackStoryOfDayReadClick(story.slug)}>
                        📖 Read the full story — free
                      </Link>
                    )}
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={handleListen}>
                      {isPlaying ? '⏹ Stop' : '🔊 Listen'}
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* CTA under the card */}
          <div className="text-center mt-4">
            <Link to="/register" className="btn btn-warning btn-lg px-5 rounded-pill fw-bold"
              onClick={() => trackLandingCta('Story of the Day CTA', '/register')}>
              ✨ Create a story like this for your child — Free
            </Link>
            <div className="mt-2" style={{color:'var(--text-soft)', fontSize:'13px'}}>
              5 free personalised stories every month · No credit card
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoryOfTheDay;
